<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useAgentStore } from '../stores/agent'
import { useAgentHealthStore } from '../stores/agent_health'
import TodayCard from './TodayCard.vue'

// "Factory" on Today: what needs you first (repos whose product-owner
// loop is paused or failing, or an unreachable agent box), then the
// sessions running right now. Quiet when the factory is idle and healthy.
const PREVIEW_LIMIT = 3

const { sessions } = storeToRefs(useAgentStore())
const { pausedRepos, failedRepos, unreachable } = storeToRefs(useAgentHealthStore())

interface FactoryRow {
  key: string
  repo: string
  title: string
  status: string
  statusClass: string
  to: string
}

const problemRows = computed<FactoryRow[]>(() => [
  ...failedRepos.value.map((r) => ({
    key: `failed-${r.repo}`,
    repo: r.repo,
    title: 'Product-owner run failed',
    status: 'Failed',
    statusClass: 'bg-status-alert/15 text-status-alert',
    to: '/admin/agent-repos',
  })),
  ...pausedRepos.value.map((r) => ({
    key: `paused-${r.repo}`,
    repo: r.repo,
    title: 'Product-owner loop paused',
    status: 'Paused',
    statusClass: 'bg-attention/15 text-attention',
    to: '/admin/agent-repos',
  })),
])

const runningRows = computed<FactoryRow[]>(() =>
  sessions.value
    .filter((s) => s.running)
    .map((s) => ({
      key: `session-${s.thread_id}`,
      repo: s.repo,
      title: s.name ?? 'Untitled session',
      status: 'Running',
      statusClass: 'bg-factory/15 text-factory',
      to: `/agent/${s.thread_id}`,
    })),
)

const rows = computed(() => [...problemRows.value, ...runningRows.value].slice(0, PREVIEW_LIMIT))
const hiddenCount = computed(() => problemRows.value.length + runningRows.value.length - rows.value.length)
</script>

<template>
  <TodayCard title="Factory" tone="factory" link-to="/agent" link-label="Sessions" testid="today-factory">
    <p
      v-if="unreachable"
      class="m-0 mb-1 rounded-lg bg-status-alert/10 px-3 py-2 text-sm text-status-alert"
      data-testid="today-factory-unreachable"
    >
      Agent status is unreachable.
    </p>
    <p v-if="rows.length === 0" class="m-0 py-3 text-ink-muted" data-testid="today-factory-empty">
      Nothing running. Dispatch a task from Repos or ⌘K.
    </p>
    <ul v-else class="m-0 list-none p-0">
      <li v-for="row in rows" :key="row.key" class="border-t border-border-divider first:border-t-0">
        <RouterLink :to="row.to" class="flex flex-col gap-1 py-3 text-ink no-underline" data-testid="today-factory-row">
          <span class="flex items-center gap-2">
            <span class="flex-1 truncate font-mono text-xs text-ink-muted">{{ row.repo }}</span>
            <span class="rounded-full px-2 py-0.5 text-xs font-semibold" :class="row.statusClass">{{
              row.status
            }}</span>
          </span>
          <span class="truncate font-medium">{{ row.title }}</span>
        </RouterLink>
      </li>
    </ul>
    <p v-if="hiddenCount > 0" class="m-0 border-t border-border-divider pt-3 text-[13px] text-ink-faint">
      {{ hiddenCount }} more in Sessions
    </p>
  </TodayCard>
</template>
